import { __extends, __rest, __assign, __makeTemplateObject } from 'tslib';
import React, { Component } from 'react';
import { styled } from 'styled-components';
import classnames from 'classnames';
import { getConditionalAriaProp } from '../../../../utils/aria/aria.js';
import { getLogger } from '../../../../utils/log/logger.js';
import { AutoId } from '../../../utils/AutoId/AutoId.js';
import '../../../../reset/HtmlA/HtmlA.js';
import { HtmlButton } from '../../../../reset/HtmlButton/HtmlButton.js';
import { HtmlDiv } from '../../../../reset/HtmlDiv/HtmlDiv.js';
import '../../../../reset/HtmlFieldSet/HtmlFieldSet.js';
import '../../../../reset/HtmlH/HtmlH.js';
import { HtmlInput } from '../../../../reset/HtmlInput/HtmlInput.js';
import '../../../../reset/HtmlLabel/HtmlLabel.js';
import '../../../../reset/HtmlLegend/HtmlLegend.js';
import '../../../../reset/HtmlLi/HtmlLi.js';
import '../../../../reset/HtmlNav/HtmlNav.js';
import '../../../../reset/HtmlOl/HtmlOl.js';
import { HtmlSpan } from '../../../../reset/HtmlSpan/HtmlSpan.js';
import '../../../../reset/HtmlTextarea/HtmlTextarea.js';
import '../../../../reset/HtmlUl/HtmlUl.js';
import '../../../../reset/HtmlTable/HtmlTable.js';
import '../../../../reset/HtmlTable/HtmlTableCaption.js';
import '../../../../reset/HtmlTable/HtmlTableHeader.js';
import '../../../../reset/HtmlTable/HtmlTableRow.js';
import '../../../../reset/HtmlTable/HtmlTableBody.js';
import '../../../../reset/HtmlTable/HtmlTableHeaderCell.js';
import '../../../../reset/HtmlTable/HtmlTableCell.js';
import { Label } from '../../Label/Label.js';
import { baseStyles } from './Dropdown.baseStyles.js';
import { SuomifiThemeConsumer } from '../../../theme/SuomifiThemeProvider/SuomifiThemeProvider.js';
import '../../../theme/SuomifiTheme/SuomifiTheme.js';
import { SpacingConsumer } from '../../../theme/SpacingProvider/SpacingProvider.js';
import { separateMarginProps } from '../../../theme/utils/spacing.js';
import { getOwnerDocument, forkRefs, filterDuplicateKeys } from '../../../../utils/common/common.js';
import { Popover } from '../../../Popover/Popover.js';
import { SelectItemList } from '../../Select/BaseSelect/SelectItemList/SelectItemList.js';
import { HintText } from '../../HintText/HintText.js';
import { StatusText } from '../../StatusText/StatusText.js';

var baseClassName = 'fi-dropdown';
var dropdownClassNames = {
  baseClassName: baseClassName,
  labelIsVisible: "".concat(baseClassName, "_label--visible"),
  inputWrapper: "".concat(baseClassName, "_input-wrapper"),
  button: "".concat(baseClassName, "_button"),
  popover: "".concat(baseClassName, "_popover"),
  itemList: "".concat(baseClassName, "_item-list"),
  item: "".concat(baseClassName, "_item"),
  statusTextHasContent: "".concat(baseClassName, "_statusText--has-content"),
  displayValue: "".concat(baseClassName, "_display-value"),
  open: "".concat(baseClassName, "--open"),
  disabled: "".concat(baseClassName, "--disabled"),
  error: "".concat(baseClassName, "--error"),
  italicize: "".concat(baseClassName, "--italicize"),
  fullWidth: "".concat(baseClassName, "--full-width")
};
var defaultProviderValue = {
  onItemClick: function onItemClick() {
    return null;
  },
  selectedDropdownValue: null,
  id: '',
  focusedItemValue: null,
  noSelectedStyles: false,
  onItemTabPress: function onItemTabPress() {
    return null;
  },
  onItemMouseOver: function onItemMouseOver() {
    return null;
  }
};
var _a = /*#__PURE__*/React.createContext(defaultProviderValue),
  DropdownProvider = _a.Provider,
  DropdownConsumer = _a.Consumer;
var BaseDropdown = function (_super) {
  __extends(BaseDropdown, _super);
  function BaseDropdown(props) {
    var _this = _super.call(this, props) || this;
    _this.state = {
      selectedValue: 'value' in _this.props ? _this.props.value : 'defaultValue' in _this.props ? _this.props.defaultValue : undefined,
      selectedValueNode: BaseDropdown.getSelectedValueNode('value' in _this.props ? _this.props.value : 'defaultValue' in _this.props ? _this.props.defaultValue : undefined, _this.props.children),
      ariaExpanded: false,
      showPopover: false,
      focusedDescendantId: 'value' in _this.props && !!_this.props.value ? _this.props.value : 'defaultValue' in _this.props ? _this.props.defaultValue : _this.getFirstItemValue(),
      preventListScrolling: false
    };
    _this.handleSpaceAndEnter = function (popoverItems, getNextItem) {
      var _a = _this.state,
        focusedDescendantId = _a.focusedDescendantId,
        showPopover = _a.showPopover;
      if (!showPopover) {
        _this.openPopover();
        if (!focusedDescendantId) {
          var nextItem = getNextItem();
          if (nextItem) {
            _this.setState({
              focusedDescendantId: nextItem.props.value
            });
          }
        }
      } else if (showPopover && focusedDescendantId) {
        var focusedItem = popoverItems.find(function (item) {
          return (item === null || item === void 0 ? void 0 : item.props.value) === focusedDescendantId;
        });
        if (focusedItem && !focusedItem.props.disabled) {
          _this.handleItemSelection(focusedItem.props.value);
        }
      }
    };
    _this.handleKeyDown = function (event) {
      _this.setState({
        preventListScrolling: false
      });
      var _a = _this.state,
        focusedDescendantId = _a.focusedDescendantId,
        ariaExpanded = _a.ariaExpanded,
        showPopover = _a.showPopover;
      var items = Array.isArray(_this.props.children) ? _this.props.children : _this.props.children !== undefined ? [_this.props.children] : undefined;
      if (!items) return;
      var popoverItems = items.flat();
      var index = !!focusedDescendantId ? popoverItems.findIndex(function (item) {
        return (item === null || item === void 0 ? void 0 : item.props.value) === focusedDescendantId;
      }) : null;
      var getNextIndex = function getNextIndex() {
        return index !== null ? (index + 1) % popoverItems.length : 0;
      };
      var getPreviousIndex = function getPreviousIndex() {
        return index !== null && index !== -1 ? (index - 1 + popoverItems.length) % popoverItems.length : popoverItems.length - 1;
      };
      var getNextItem = function getNextItem() {
        return popoverItems[getNextIndex()];
      };
      var getPreviousItem = function getPreviousItem() {
        return popoverItems[getPreviousIndex()];
      };
      switch (event.key) {
        case 'ArrowDown':
          {
            event.preventDefault();
            if (!showPopover) {
              _this.openPopover();
            } else {
              var nextItem = getNextItem();
              if (nextItem) {
                _this.setState({
                  focusedDescendantId: nextItem.props.value
                });
              }
            }
            break;
          }
        case 'ArrowUp':
          {
            event.preventDefault();
            if (!showPopover) {
              _this.openPopover();
            } else {
              var previousItem = getPreviousItem();
              if (previousItem) {
                _this.setState({
                  focusedDescendantId: previousItem.props.value
                });
              }
            }
            break;
          }
        case ' ':
          {
            event.preventDefault();
            _this.setState({
              showPopover: !showPopover,
              ariaExpanded: !ariaExpanded
            });
            _this.handleSpaceAndEnter(popoverItems, getNextItem);
            break;
          }
        case 'Enter':
          {
            event.preventDefault();
            _this.handleSpaceAndEnter(popoverItems, getNextItem);
            break;
          }
        case 'Escape':
          {
            if (showPopover) {
              event.stopPropagation();
              _this.focusToButtonAndClosePopover();
            }
            break;
          }
      }
    };
    _this.handleOnBlur = function () {
      var ownerDocument = getOwnerDocument(_this.buttonRef);
      if (!ownerDocument) {
        return;
      }
      requestAnimationFrame(function () {
        var _a, _b;
        var focusInPopover = (_a = _this.popoverRef.current) === null || _a === void 0 ? void 0 : _a.contains(ownerDocument.activeElement);
        if (focusInPopover) {
          return;
        }
        if (!!_this.props.onBlur) {
          _this.props.onBlur();
        }
        var focusInToggleButton = (_b = _this.buttonRef.current) === null || _b === void 0 ? void 0 : _b.contains(ownerDocument.activeElement);
        var focusInDropdown = focusInPopover || focusInToggleButton;
        if (!focusInDropdown) {
          _this.setState({
            showPopover: focusInDropdown,
            ariaExpanded: focusInDropdown
          });
        }
      });
    };
    _this.buttonRef = /*#__PURE__*/React.createRef();
    _this.popoverRef = /*#__PURE__*/React.createRef();
    return _this;
  }
  BaseDropdown.getDerivedStateFromProps = function (nextProps, prevState) {
    var value = nextProps.value;
    if ('value' in nextProps && value !== prevState.selectedValue) {
      return {
        selectedValue: value,
        selectedValueNode: BaseDropdown.getSelectedValueNode(value, nextProps.children)
      };
    }
    if (prevState.selectedValue && nextProps.children && BaseDropdown.valueExistsInChildren(prevState.selectedValue, nextProps.children)) {
      return {
        selectedValue: prevState.selectedValue,
        selectedValueNode: BaseDropdown.getSelectedValueNode(prevState.selectedValue, nextProps.children)
      };
    }
    return null;
  };
  BaseDropdown.getSelectedValueNode = function (selectedValue, children) {
    if (selectedValue === undefined || children === undefined) return undefined;
    if (Array.isArray(children)) {
      var flatChildren = children.flat();
      for (var index = 0; index < flatChildren.length; index += 1) {
        var element = flatChildren[index];
        if (element.props.value === selectedValue) {
          return element.props.children;
        }
      }
    } else {
      return children.props.children;
    }
  };
  BaseDropdown.valueExistsInChildren = function (value, children) {
    if (Array.isArray(children)) {
      var flatChildren = children.flat();
      return flatChildren.some(function (child) {
        return child.props.value === value;
      });
    }
    return children.props.value === value;
  };
  BaseDropdown.prototype.componentDidMount = function () {
    this.componentIsMounted = true;
  };
  BaseDropdown.prototype.componentWillUnmount = function () {
    this.componentIsMounted = false;
  };
  BaseDropdown.prototype.isOutsideClick = function (event) {
    return !!this.buttonRef && this.buttonRef.current.contains(event.target);
  };
  BaseDropdown.prototype.handleItemSelection = function (itemValue) {
    var _this = this;
    if (!!this.props.onChange) {
      this.props.onChange(itemValue);
    }
    this.setState({
      selectedValue: itemValue,
      selectedValueNode: BaseDropdown.getSelectedValueNode(itemValue, this.props.children),
      focusedDescendantId: itemValue,
      ariaExpanded: false
    });
    setTimeout(function () {
      var _a;
      if (_this.componentIsMounted) {
        (_a = _this.buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        _this.setState({
          showPopover: false
        });
      }
    }, 10);
  };
  BaseDropdown.prototype.getFirstItemValue = function () {
    if (Array.isArray(this.props.children)) {
      var element = this.props.children[0];
      if (Array.isArray(element)) {
        return element[0].props.value;
      }
      return element.props.value;
    }
    if (!!this.props.children) {
      return this.props.children.props.value;
    }
    return null;
  };
  BaseDropdown.prototype.getDisplayValue = function () {
    var _a;
    if (this.props.alwaysShowVisualPlaceholder) {
      return this.props.visualPlaceholder;
    }
    return (_a = this.state.selectedValueNode) !== null && _a !== void 0 ? _a : this.props.visualPlaceholder;
  };
  BaseDropdown.prototype.focusToButtonAndClosePopover = function () {
    var _a;
    this.setState({
      ariaExpanded: false
    });
    (_a = this.buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    this.setState({
      showPopover: false
    });
  };
  BaseDropdown.prototype.openPopover = function () {
    var _this = this;
    this.setState({
      showPopover: true,
      ariaExpanded: true
    });
    setTimeout(function () {
      var _a;
      (_a = _this.popoverRef.current) === null || _a === void 0 ? void 0 : _a.focus();
    }, 200);
  };
  BaseDropdown.prototype.render = function () {
    var _a, _b, _c;
    var _this = this;
    var _d = this.props,
      id = _d.id,
      name = _d.name,
      disabled = _d.disabled,
      children = _d.children,
      labelText = _d.labelText,
      labelMode = _d.labelMode,
      forwardedRef = _d.forwardedRef,
      optionalText = _d.optionalText,
      ariaLabelledBy = _d["aria-labelledby"],
      hintText = _d.hintText,
      status = _d.status,
      statusText = _d.statusText,
      visualPlaceholder = _d.visualPlaceholder,
      alwaysShowVisualPlaceholder = _d.alwaysShowVisualPlaceholder,
      className = _d.className;
      _d.onChange;
      _d.onBlur;
      var tooltipComponent = _d.tooltipComponent,
      _e = _d.portal,
      portal = _e === void 0 ? true : _e,
      _f = _d.statusTextAriaLiveMode,
      statusTextAriaLiveMode = _f === void 0 ? 'assertive' : _f,
      fullWidth = _d.fullWidth,
      style = _d.style,
      rest = __rest(_d, ["id", "name", "disabled", "children", "labelText", "labelMode", "forwardedRef", "optionalText", 'aria-labelledby', "hintText", "status", "statusText", "visualPlaceholder", "alwaysShowVisualPlaceholder", "className", "onChange", "onBlur", "tooltipComponent", "portal", "statusTextAriaLiveMode", "fullWidth", "style"]);
    var _g = separateMarginProps(rest),
      passProps = _g[1];
    if (React.Children.count(children) < 1) {
      getLogger().warn("Dropdown '".concat(labelText, "' does not contain items"));
      return null;
    }
    var _h = this.state,
      selectedValue = _h.selectedValue,
      ariaExpanded = _h.ariaExpanded,
      showPopover = _h.showPopover,
      focusedDescendantId = _h.focusedDescendantId,
      preventListScrolling = _h.preventListScrolling;
    var labelId = "".concat(id, "-label");
    var buttonId = "".concat(id, "_button");
    var popoverItemListId = "".concat(id, "-popover");
    var hintTextId = hintText ? "".concat(id, "-hintText") : undefined;
    var statusTextId = statusText ? "".concat(id, "-statusText") : undefined;
    var displayValueId = "".concat(id, "-displayValue");
    var ariaActiveDescendant = focusedDescendantId ? "".concat(id, "-").concat(focusedDescendantId) : '';
    var dropdownDisplayValue = this.getDisplayValue();
    var italicize = visualPlaceholder && !alwaysShowVisualPlaceholder && !selectedValue;
    var definedRef = forwardedRef || null;
    return /*#__PURE__*/React.createElement(HtmlDiv, {
      className: classnames(className, baseClassName, (_a = {}, _a[dropdownClassNames.disabled] = !!disabled, _a[dropdownClassNames.open] = !!showPopover, _a[dropdownClassNames.error] = status === 'error', _a[dropdownClassNames.italicize] = italicize, _a[dropdownClassNames.fullWidth] = fullWidth, _a)),
      id: id,
      style: style
    }, /*#__PURE__*/React.createElement(Label, {
      id: labelId,
      labelMode: labelMode,
      optionalText: optionalText,
      className: classnames((_b = {}, _b[dropdownClassNames.labelIsVisible] = labelMode !== 'hidden', _b)),
      tooltipComponent: tooltipComponent,
      onClick: function onClick() {
        var _a;
        if (!disabled) {
          (_a = _this.buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
      }
    }, labelText), /*#__PURE__*/React.createElement(HintText, {
      id: hintTextId
    }, hintText), /*#__PURE__*/React.createElement(HtmlDiv, {
      className: dropdownClassNames.inputWrapper
    }, /*#__PURE__*/React.createElement(HtmlButton, __assign({
      "aria-haspopup": "listbox",
      tabIndex: !disabled ? 0 : -1,
      forwardedRef: forkRefs(this.buttonRef, definedRef),
      id: buttonId,
      className: dropdownClassNames.button
    }, getConditionalAriaProp('aria-labelledby', [displayValueId, ariaLabelledBy, labelId]), getConditionalAriaProp('aria-describedby', [statusTextId, hintTextId]), {
      "aria-controls": popoverItemListId,
      "aria-expanded": ariaExpanded,
      onClick: function onClick() {
        if (!showPopover) {
          _this.openPopover();
        } else {
          _this.focusToButtonAndClosePopover();
        }
      },
      onKeyDown: this.handleKeyDown,
      onBlur: this.handleOnBlur
    }, passProps), /*#__PURE__*/React.createElement(HtmlSpan, {
      className: dropdownClassNames.displayValue,
      id: displayValueId
    }, dropdownDisplayValue), /*#__PURE__*/React.createElement(HtmlInput, {
      tabIndex: -1,
      type: "hidden",
      name: name,
      value: selectedValue || ''
    })), /*#__PURE__*/React.createElement(StatusText, {
      id: statusTextId,
      className: classnames((_c = {}, _c[dropdownClassNames.statusTextHasContent] = !!statusText, _c)),
      status: status,
      disabled: disabled,
      ariaLiveMode: statusTextAriaLiveMode
    }, statusText), showPopover && ( /*#__PURE__*/React.createElement(Popover, {
      sourceRef: this.buttonRef,
      onClickOutside: function onClickOutside(event) {
        if (!_this.isOutsideClick(event)) {
          _this.setState({
            showPopover: false,
            ariaExpanded: false
          });
        }
      },
      matchWidth: true,
      onKeyDown: this.handleKeyDown,
      portal: portal
    }, /*#__PURE__*/React.createElement(DropdownProvider, {
      value: {
        onItemClick: function onItemClick(itemValue) {
          return _this.handleItemSelection(itemValue);
        },
        selectedDropdownValue: selectedValue,
        id: id,
        focusedItemValue: focusedDescendantId,
        noSelectedStyles: alwaysShowVisualPlaceholder,
        onItemTabPress: function onItemTabPress() {
          return _this.focusToButtonAndClosePopover();
        },
        onItemMouseOver: function onItemMouseOver(itemValue) {
          return _this.setState({
            preventListScrolling: true,
            focusedDescendantId: itemValue
          });
        }
      }
    }, /*#__PURE__*/React.createElement(SelectItemList, {
      id: popoverItemListId,
      ref: this.popoverRef,
      focusedDescendantId: ariaActiveDescendant,
      className: dropdownClassNames.itemList,
      onKeyDown: function onKeyDown(event) {
        if (event.key === 'Tab') {
          event.preventDefault();
          _this.focusToButtonAndClosePopover();
        }
      },
      preventScrolling: preventListScrolling
    }, children || []))))));
  };
  return BaseDropdown;
}(Component);
var StyledDropdown = styled(function (_a) {
  _a.theme;
    _a.globalMargins;
    var passProps = __rest(_a, ["theme", "globalMargins"]);
  return /*#__PURE__*/React.createElement(BaseDropdown, __assign({}, passProps));
}).withConfig({
  componentId: "sc-15kwal2-0"
})(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), function (_a) {
  var theme = _a.theme,
    globalMargins = _a.globalMargins,
    rest = __rest(_a, ["theme", "globalMargins"]);
  var _b = separateMarginProps(rest),
    marginProps = _b[0];
  var cleanedGlobalMargins = filterDuplicateKeys(globalMargins.dropdown, marginProps);
  return baseStyles(theme, cleanedGlobalMargins, marginProps);
});
var DropdownInner = function DropdownInner(props, ref) {
  var propId = props.id,
    passProps = __rest(props, ["id"]);
  return /*#__PURE__*/React.createElement(SpacingConsumer, null, function (_a) {
    var margins = _a.margins;
    return /*#__PURE__*/React.createElement(SuomifiThemeConsumer, null, function (_a) {
      var suomifiTheme = _a.suomifiTheme;
      return /*#__PURE__*/React.createElement(AutoId, {
        id: propId
      }, function (id) {
        return /*#__PURE__*/React.createElement(StyledDropdown, __assign({
          theme: suomifiTheme,
          globalMargins: margins,
          id: id,
          forwardedRef: ref
        }, passProps));
      });
    });
  });
};
var Dropdown = /*#__PURE__*/React.forwardRef(DropdownInner);
DropdownInner.displayName = 'Dropdown';
var templateObject_1;

export { Dropdown, DropdownConsumer, DropdownProvider, dropdownClassNames };
//# sourceMappingURL=Dropdown.js.map
